import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Input, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  maxTablet,
  maxWidth,
  primaryColor,
  primaryFont,
  primaryHeadingColor, retina, secondaryFont, wLight, wMBold, wMedium,
} from 'Utils/utilsStyle';
import {
  selectReasonPopupLoading,
} from 'Selector/BuyerGroupSelector';

import { actions as certificationActions } from 'Slice/CertificationSlice';
import { selectData } from 'Selector/CertificationSelector';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { valueToObject } from 'Utils/UtilityFunctions';
import { selectSocialCall, selectSocialFlurry } from 'Selector/PersonProfileSelector';
import CrossCircle from '../assets/svgIcons/CrossCircle';

const Wrapper = styled.div`
  .reason-modal {
    .ant-modal {
      @media all and (max-width: ${maxWidth}), ${retina} {
        width: 535px !important;
      }
      @media all and (max-width: ${maxTablet}) {
        width: 350px !important;
      }
      .ant-modal-content {
        .ant-modal-close {
          .ant-modal-close-x {
            display: block;
            .cross-icon-x {
              .cross-circle-svg  {
                @media all and (max-width: ${maxTablet}) {
                  width: 20px;
                  height: 20px;
                  margin: 17px 10px 0px 0px;
                }
              }
            }
          }
        }
        .ant-modal-footer {
          display: none;
          &.custom-footer {
            display: block;
            padding: 40px 10px 48px 10px;
            @media all and (max-width: ${maxWidth}), ${retina} {
              padding: 20px 10px 20px;
            }
          }
        }
        .ant-modal-body{
          padding: 32px 24px 24px 24px;
          text-align: unset;
          .action-btn {
            font-family: ${primaryFont};
            font-weight: ${wMBold};
            background-color: ${primaryColor};
            border-color:${primaryColor};
            border:2px solid ${primaryColor};
            padding: 3px 7px;
            display: inline-block;
            margin-right: 5px;
            border-radius: 18px;
            color:#fff;
            cursor:pointer;
            font-size: 16px;
            min-width: 108px;
            @media all and (max-width: ${maxWidth}), ${retina} {
              font-size: 14px;
              min-width: 95px;
            }
            &.close{
              background-color: #fff;
              color : ${primaryColor};
              margin-left: 5px;
              margin-right: 0px;
            }
          }
          
          .ant-input-textarea-affix-wrapper{
            @media all and (max-width: ${maxTablet}) {
              height: 100px !important;
            }
            .ant-input{
              border-color: ${primaryColor};
              box-shadow: 0 0 0 1px ${primaryColor};
              color: ${primaryHeadingColor};
              padding: 32px 26px 32px 16px;
              font-style: italic;
              font-weight: ${wLight};
              font-size: 18px;
              line-height: 25px;
              font-family: ${primaryFont};
              resize: none;
              @media all and (max-width: ${maxWidth}), ${retina} {
                font-size: 16px;
                line-height: 20px;
                padding: 10px 15px 23px 10px;
              }
              @media all and (max-width: ${maxTablet}) {
                font-size: 14px;
                line-height: 16px;
              }
            }
            .ant-input::placeholder {
              @media all and (max-width: ${maxWidth}), ${retina} {
                font-size: 16px;
                line-height: 20px;
              }
              @media all and (max-width: ${maxTablet}) {
                font-size: 14px;
                line-height: 16px;
              }
            }
            &.ant-input-textarea-show-count {
              .ant-input-data-count {
                bottom: 5px;
                right: 10px;
                font-size: 11px;
                font-family: ${primaryFont};
                font-style: italic;
              }
            }
          }
        }
      }
    }
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
  .spinner-layer {
    position: absolute;
    top: 0px;
    left: 0px;
    height: 100%;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    background-color: rgba(249, 248, 246, 0.5);
    z-index: 1;
  }
`;

const HeadingPopup = styled.h4`
  font-family: ${secondaryFont};
  font-weight: ${wMBold};
  font-size: 32px;
  line-height: 32px;
  letter-spacing: -0.8px;
  color: ${primaryHeadingColor};
  margin-bottom: 24px;
  @media all and (max-width: ${maxWidth}), ${retina} {
    font-size: 28px;
    line-height: 32px;
    margin-bottom: 12px;
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 24px;
    line-height: 26px;
    margin-bottom: 6px;
  }
  `;

const Description = styled.h4`
  font-family: ${secondaryFont};
  font-weight: ${wMedium};
  font-size: 22px;
  line-height: 24px;
  color: #67696A;
  margin-bottom: 16px;
  @media all and (max-width: ${maxWidth}), ${retina} {
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 12px;
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 16px;
    line-height: 18px;
  }
`;

type Props = {
  isShareReason?: boolean;
  visible?: boolean;
  setVisible?: () => void;
  categoryNext?: string;
  categoryPrev?: string;
  stageNext?: string;
  stagePrev?: string;
  handleChangeCategory: (a: string) => void;
} & typeof defaultProps;

const defaultProps = {
  isShareReason: false,
  visible: false,
  setVisible: {},
  categoryNext: '',
  categoryPrev: '',
  stageNext: '',
  stagePrev: '',
};

const AddReasonPopup = function AddReasonPopup(props: Props) {
  const {
    visible, setVisible, categoryNext, categoryPrev, stageNext, stagePrev, handleChangeCategory,
    isShareReason,
  } = props;
  const spinner = useSelector(selectReasonPopupLoading);
  const flurryObject = useSelector(selectSocialFlurry);
  const isCalled = useSelector(selectSocialCall);
  const elementRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const { TextArea } = Input;
  const [reason, setReason] = useState<string>('');
  const [disable, setDisable] = useState<boolean>(false);
  const host = window.location.href;
  const dispatch = useDispatch();

  useEffect(() => {
    if (reason.trim() === '') {
      setDisable(true);
    } else {
      setDisable(false);
    }
  }, [reason]);

  useEffect(() => {
    if (visible) {
      setTimeout(
        () => reasonRef.current?.focus(),
        200,
      );
    }
  }, [visible]);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  };

  const onClickCancel = () => {
    setReason('');
    setVisible();
  };

  const handleSubmit = () => {
    setReason('');
    setVisible();
    handleChangeCategory(reason);
  };

  const data = useSelector(selectData);

  const handleClick = () => {
    handleSubmit();
    dispatch(certificationActions.certificationAction({
      type: 'reason_enter',
      source: 'buyer',
      data,
    }));
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('change_bgroup_role_complete', 0, {
        ...flurryObject,
        isDeleteTime: true,
        ...isCalled && { isCalled },
        host,
      }),
    ));
  };

  const placeholder = isShareReason ? ('Shared to give Sales Rep context on buyer intent before deep dive.') : ("Add a note to signify why the person's assigned buyer group category was changed. The category will only be changed for this group!");
  return (
    <Wrapper ref={elementRef}>
      <Modal
        getContainer={elementRef.current || ''}
        open={visible}
        onCancel={setVisible}
        centered
        destroyOnClose
        maskClosable
        width={689}
        closeIcon={<div className="cross-icon-x"><CrossCircle /></div>}
        wrapClassName="reason-modal"
      >
        {spinner
          && (
            <div className="spinner-layer">
              <Spin />
            </div>
          )}
        <HeadingPopup>
          Add reason
        </HeadingPopup>
        {isShareReason ? (
          <Description>
            for sharing group
          </Description>
        ) : (
          <Description>
            Move from
            {' '}
            {categoryPrev}
            {' '}
            {stagePrev}
            {' '}
            to
            {' '}
            {categoryNext}
            {' '}
            {stageNext}
          </Description>
        )}
        <TextArea
          showCount
          maxLength={150}
          style={{ height: 150 }}
          value={reason}
          onChange={onChange}
          placeholder={placeholder}
          ref={reasonRef}
        />
        <div className="ant-modal-footer custom-footer">
          <span
            tabIndex={0}
            role="button"
            aria-label="done-button"
            className={`action-btn ${disable ? 'disabled' : ''}`}
            onClick={handleClick}
            onKeyPress={handleClick}
          >
            Done
          </span>
          <span
            tabIndex={0}
            role="button"
            aria-label="close"
            className="action-btn close"
            onClick={onClickCancel}
            onKeyPress={onClickCancel}
          >
            Cancel
          </span>
        </div>
      </Modal>
    </Wrapper>
  );
};

AddReasonPopup.defaultProps = defaultProps;
export default AddReasonPopup;
